import { ActionIcon, Badge, Box, Checkbox, Group, Stack, Text, Tooltip } from "@mantine/core";
import { IconChevronRight } from "@tabler/icons-react";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { createContext, useContext, useId, useState } from "react";
import { httpPostGraphql } from "shared/api/httpClient";
import { qk } from "shared/utils/qk";
import { graphql } from "src/gql";

type TopicEditorNode = {
    id: string;
    name: string;
    isCategory: boolean;
    isRequired: boolean;
    children?: TopicEditorNode[];
};

type TopicTreeSelectContext = {
    disableRequired: boolean;
    showRequiredLabel?: boolean;
    values: { id: string; name: string }[];
    expandedIds: string[];
    expandNode: (id: string) => void;
    collapseNode: (id: string) => void;
    onCheckboxChange: (target: { id: string; name: string }, checked: boolean) => void;
};
const ctx = createContext<TopicTreeSelectContext | null>(null);

type TopicTreeSelectProps = {
    /** When set, topics with `isRequired` set to true will be checked and disabled */
    disableRequired?: boolean;
    showRequiredLabel?: boolean;
    values: { id: string; name: string }[];
    onChange?: (topics: { id: string; name: string }[]) => unknown;
    onTopicChange?: (topic: { id: string; name: string }, checked: boolean) => unknown;
};

export const TopicTreeSelect = (props: TopicTreeSelectProps) => {
    const {
        data: { rootTopic },
    } = useSuspenseQuery(query);
    const [expandedIds, setExpandedIds] = useState([] as string[]);

    function expandNode(id: string) {
        setExpandedIds((prev) => [...new Set(prev.concat(id))]);
    }

    function collapseNode(id: string) {
        setExpandedIds((prev) => prev.filter((it) => it != id));
    }

    function onCheckboxChange(target: { id: string; name: string }, checked: boolean) {
        props.onTopicChange?.(target, checked);
        if (checked) props.onChange?.(props.values.concat([target]));
        else props.onChange?.(props.values.filter((it) => it.id != target.id));
    }

    return (
        <ctx.Provider
            value={{
                values: props.values,
                showRequiredLabel: props.showRequiredLabel,
                expandNode,
                collapseNode,
                expandedIds,
                onCheckboxChange,
                disableRequired: !!props.disableRequired,
            }}
        >
            <Stack gap={0}>
                {rootTopic.children.map((topic) => (
                    <TopicNode key={topic.id} node={topic} />
                ))}
            </Stack>
        </ctx.Provider>
    );
};

const TopicNode = (props: { node: TopicEditorNode }) => {
    const { collapseNode, expandNode, onCheckboxChange, expandedIds, values, disableRequired, showRequiredLabel } = useContext(ctx)!;

    const isExpanded = expandedIds.includes(props.node.id);
    const textId = useId();

    return (
        <Stack gap={0} display="inline-flex" w="100%">
            <Group gap="0" p="sm" wrap="nowrap" style={{ whiteSpace: "nowrap" }}>
                <ActionIcon
                    display={props.node.isCategory && (props.node.children?.length ?? 0) > 0 ? undefined : "none"}
                    variant="transparent"
                    color="text"
                    onClick={() => {
                        if (isExpanded) collapseNode(props.node.id);
                        else expandNode(props.node.id);
                    }}
                    aria-label="Show or hide subtopics"
                >
                    <IconChevronRight
                        size={16}
                        style={{ transition: "transform 0.5s", transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)" }}
                    />
                </ActionIcon>
                {!props.node.isCategory && (
                    <Tooltip
                        disabled={!props.node.isRequired || !disableRequired}
                        label="This topic is required and cannot be unsubscribed from"
                    >
                        <Checkbox
                            disabled={props.node.isRequired && disableRequired}
                            checked={(props.node.isRequired && disableRequired) || values.some((it) => it.id == props.node.id)}
                            onChange={(e) => onCheckboxChange(props.node, e.target.checked)}
                            pr="sm"
                            aria-labelledby={textId}
                        />
                    </Tooltip>
                )}
                <Group wrap="nowrap" w="100%">
                    <Text id={textId} style={{ textWrap: "balance" }}>
                        {props.node.name}
                    </Text>
                    {props.node.isRequired && showRequiredLabel && <Badge miw="fit-content">Required</Badge>}
                </Group>
            </Group>
            <Stack gap={0} style={{ display: isExpanded ? undefined : "none" }} w="100%">
                <Box
                    w="fit-content"
                    style={{
                        borderLeft: "3rem solid transparent",
                        overflow: "auto",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    {props.node.children?.map((c) => <TopicNode key={c.id} node={c} />)}
                </Box>
            </Stack>
        </Stack>
    );
};

const query = queryOptions({
    queryKey: qk("topicTree", "list"),
    queryFn: () =>
        httpPostGraphql(
            graphql(`
                query TopicTreeTopics {
                    rootTopic {
                        id
                        name
                        isCategory
                        isRequired
                        children {
                            id
                            name
                            isCategory
                            isRequired
                            children {
                                id
                                name
                                isCategory
                                isRequired
                                children {
                                    id
                                    name
                                    isCategory
                                    isRequired
                                    children {
                                        id
                                        name
                                        isCategory
                                        isRequired
                                    }
                                }
                            }
                        }
                    }
                }
            `),
            {},
        ),
});
